/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import SpeedIcon from "@mui/icons-material/Speed";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SettingsIcon from "@mui/icons-material/Settings";
import { SyntheticEvent, useEffect, useState } from "react";
import { Location } from "react-router-dom";
import { Vocabulary } from "../../Utils/Vocabulary";
import { GenericModel } from "../../Models/Models";

interface VehicleDetailsFilterCollapsibleProps {
  data: any;
  cacheContext: any;
  location: Location;
  getValuesForMultipleAutocomplete: (value: string) => GenericModel[];
  handleChangeMultipleAutocomplete: (
    event: SyntheticEvent<Element, Event>,
    newValue: GenericModel[],
    changingValue: string
  ) => void;
  handleAccordion: (open: boolean) => void;
}

export default function VehicleDetailsFilterCollapsible(
  props: VehicleDetailsFilterCollapsibleProps
) {
  const {
    data,
    cacheContext,
    location,
    getValuesForMultipleAutocomplete,
    handleChangeMultipleAutocomplete,
    handleAccordion,
  } = props;

  const [expanded, setExpanded] = useState(
    (location.state as any)?.openCollapsable === true
  );

  // Watch location changes and update expanded state
  useEffect(() => {
    if ((location.state as any)?.openCollapsable === true) {
      setExpanded(true);
    }
  }, [location]);

  /**
   * Get selected brand IDs from data
   */
  const getSelectedBrandIds = (): number[] => {
    try {
      return data.brand ? JSON.parse(data.brand) : [];
    } catch {
      return [];
    }
  };

  /**
   * Get filtered models by selected brands
   */
  const getFilteredModels = (): any[] => {
    const selectedBrandIds = getSelectedBrandIds();
    const allModels = cacheContext.cache.models || [];

    if (selectedBrandIds.length === 0) return [];
    return allModels.filter((m: any) => selectedBrandIds.includes(m.brand_id));
  };

  /**
   * Show brand name if duplicate model names
   */
  const getModelLabel = (option: any): string => {
    if (!option) return "";
    const filtered = getFilteredModels();
    const duplicates =
      filtered.filter((m: any) => m.nume === option.nume).length > 1;

    if (duplicates) {
      const brand = (cacheContext.cache.brands || []).find(
        (b: any) => b.id === option.brand_id
      );
      return brand ? `${option.nume} (${brand.nume})` : option.nume;
    }
    return option.nume;
  };

  /**
   * Get gearbox options
   */
  const getGearboxOptions = () => {
    return cacheContext.cache.gearboxes || [];
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_: any, isExpanded: boolean) => setExpanded(isExpanded)}
      sx={{
        mb: 1,
        boxShadow: 0,
        backgroundColor: "none !important",
        border: "0 !important",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="vehicle-details-content"
        id="vehicle-details-header"
        sx={{
          backgroundColor: "#f7f8fa !important",
          border: "0 !important",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <Typography sx={{ fontWeight: 500, color: "#4d5e80" }}>
          {Vocabulary.vehicleDetailsText}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          pt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: 0,
          backgroundColor: "#f7f8fa !important",
        }}
      >
        {/* Brand Filter */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DirectionsCarIcon
            sx={{ color: "#4d5e80", mr: 1.5, flexShrink: 0 }}
          />
          <Autocomplete
            id="brand"
            multiple
            freeSolo={false}
            disablePortal
            fullWidth
            getOptionLabel={(option: any) => option.nume}
            size="small"
            options={cacheContext.cache.brands || []}
            value={getValuesForMultipleAutocomplete("brand") ?? []}
            onChange={(event: any, newValue: any) => {
              handleChangeMultipleAutocomplete(event, newValue, "brand");
              handleAccordion(false);
            }}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.brand}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Box>

        {/* Model Filter */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <SpeedIcon sx={{ color: "#4d5e80", mr: 1.5, flexShrink: 0 }} />
          <Autocomplete
            id="model"
            multiple
            freeSolo={false}
            disablePortal
            fullWidth
            getOptionLabel={getModelLabel}
            size="small"
            options={getFilteredModels()}
            value={getValuesForMultipleAutocomplete("model") ?? []}
            onChange={(event: any, newValue: any) => {
              handleChangeMultipleAutocomplete(event, newValue, "model");
              handleAccordion(false);
            }}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.selectModel}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Box>

        {/* Body Type Filter */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <LocalShippingIcon
            sx={{ color: "#4d5e80", mr: 1.5, flexShrink: 0 }}
          />
          <Autocomplete
            id="bodyType"
            multiple
            freeSolo={false}
            disablePortal
            fullWidth
            getOptionLabel={(option: any) => option.nume}
            size="small"
            options={cacheContext.cache.bodyworkTypes || []}
            value={getValuesForMultipleAutocomplete("bodyType") ?? []}
            onChange={(event: any, newValue: any) => {
              handleChangeMultipleAutocomplete(event, newValue, "bodyType");
              handleAccordion(false);
            }}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.bodyType}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Box>

        {/* Gearbox Dropdown */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <SettingsIcon sx={{ color: "#4d5e80", mr: 1.5, flexShrink: 0 }} />
          <Autocomplete
            id="gearbox"
            multiple
            freeSolo={false}
            disablePortal
            fullWidth
            getOptionLabel={(option: any) => option.nume}
            size="small"
            options={getGearboxOptions()}
            value={getValuesForMultipleAutocomplete("gearbox") ?? []}
            onChange={(event: any, newValue: any) => {
              handleChangeMultipleAutocomplete(event, newValue, "gearbox");
              handleAccordion(false);
            }}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={Vocabulary.transmission}
                variant="outlined"
                size="small"
              />
            )}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
